import { TechnicalError } from '@orus.eu/error'
import type { Prettify } from '@orus.eu/utils'

import type { Label } from './label.ts'
import {
  isAcceptedAnswer,
  type Answers,
  type AnswersOf,
  type AnswerValue,
  type Question,
  type SingleChoiceQuestion,
  type SingleChoiceSpecification,
} from './question.ts'

export type Screen<QuestionId extends string, LabelParameters> = {
  readonly id: string
  readonly title: Label<LabelParameters>
  readonly tooltip: Label<LabelParameters> | undefined
  readonly questions: readonly QuestionId[]
}

export type StandaloneFormDefinition<LabelParameters, Questions extends Readonly<Record<string, Question<never>>>> = {
  readonly id: string
  /** Bumped on every change, and sent with every submission. */
  readonly version: string
  /** A sensitive form renders inside a wrapper that keeps its answers out of session recordings. */
  readonly sensitive: boolean
  readonly questions: Questions
  readonly screens: readonly Screen<keyof Questions & string, LabelParameters>[]
}

export type AnyStandaloneFormDefinition = StandaloneFormDefinition<never, Readonly<Record<string, Question<never>>>>

export type StandaloneFormQuestionId<Definition extends AnyStandaloneFormDefinition> = keyof Definition['questions'] &
  string

/** The answers typed by the definition, as `parseAnswers` returns them. */
export type StandaloneFormAnswers<Definition extends AnyStandaloneFormDefinition> = AnswersOf<Definition['questions']>

type StandaloneFormHeader = {
  readonly id: string
  readonly version: string
  readonly sensitive: boolean
}

type ScreenSpecification<QuestionId extends string, LabelParameters> = {
  readonly id: string
  readonly title: Label<LabelParameters>
  readonly tooltip?: Label<LabelParameters>
  /** The screen shows while one of them is visible. */
  readonly questions: readonly QuestionId[]
}

type Questions<LabelParameters> = Readonly<Record<string, Question<LabelParameters>>>

type WithQuestion<Earlier, QuestionId extends string, NewQuestion> = Prettify<Earlier & Record<QuestionId, NewQuestion>>

export type StandaloneFormBuilder<LabelParameters, Earlier extends Questions<LabelParameters>> = {
  /** Adds a question, whose predicate reads the answers to the questions added before it. */
  readonly question: <const QuestionId extends string, const OptionId extends string>(
    questionId: QuestionId,
    specification: SingleChoiceSpecification<OptionId, LabelParameters, AnswersOf<Earlier>>,
  ) => StandaloneFormBuilder<
    LabelParameters,
    WithQuestion<Earlier, QuestionId, SingleChoiceQuestion<OptionId, LabelParameters>>
  >
  /** Ends the definition. Throws on a malformed one, which the definition's own test catches. */
  readonly screens: (
    screens: readonly ScreenSpecification<NoInfer<keyof Earlier & string>, LabelParameters>[],
  ) => StandaloneFormDefinition<LabelParameters, Earlier>
}

/**
 * Starts a definition that adds its questions one at a time, so that each predicate is typed by the answers it may
 * read. The label parameters are the values a label may quote.
 */
export function defineStandaloneForm<LabelParameters = Record<never, never>>(
  header: StandaloneFormHeader,
): StandaloneFormBuilder<LabelParameters, Record<never, never>> {
  return standaloneFormBuilder(header, {})
}

function standaloneFormBuilder<LabelParameters, Earlier extends Questions<LabelParameters>>(
  header: StandaloneFormHeader,
  questions: Earlier,
): StandaloneFormBuilder<LabelParameters, Earlier> {
  return {
    question: (questionId, specification) => {
      if (Object.hasOwn(questions, questionId)) {
        throw new TechnicalError('While defining a standalone form, found two questions with the same id', {
          context: { formId: header.id, questionId },
        })
      }
      return standaloneFormBuilder(header, withQuestion(questions, questionId, toQuestion(specification, questions)))
    },
    screens: (screens) => {
      checkScreens(header.id, screens, Object.keys(questions))
      checkOptions(questions)
      return {
        id: header.id,
        version: header.version,
        sensitive: header.sensitive,
        questions,
        screens: screens.map((screen) => ({
          id: screen.id,
          title: screen.title,
          tooltip: screen.tooltip,
          questions: screen.questions,
        })),
      }
    },
  }
}

function withQuestion<Earlier extends object, QuestionId extends string, NewQuestion>(
  questions: Earlier,
  questionId: QuestionId,
  question: NewQuestion,
): WithQuestion<Earlier, QuestionId, NewQuestion> {
  return { ...questions, ...singleEntry(questionId, question) }
}

function singleEntry<Key extends string, Value>(key: Key, value: Value): Record<Key, Value> {
  const entry: Record<string, Value> = {}
  entry[key] = value
  return entry
}

function toQuestion<OptionId extends string, LabelParameters, Earlier extends Questions<LabelParameters>>(
  specification: SingleChoiceSpecification<OptionId, LabelParameters, AnswersOf<Earlier>>,
  earlierQuestions: Earlier,
): SingleChoiceQuestion<OptionId, LabelParameters> {
  return {
    kind: 'single-choice',
    label: specification.label,
    tooltip: specification.tooltip,
    options: specification.options,
    isVisible: specification.isVisible
      ? readingAcceptedAnswers(earlierQuestions, specification.isVisible)
      : alwaysVisible,
  }
}

/** Hands a predicate the answers its questions accept, so that it never reads an unknown question or value. */
function readingAcceptedAnswers<ReadableQuestions extends Questions<never>>(
  questions: ReadableQuestions,
  predicate: (answers: AnswersOf<ReadableQuestions>) => boolean,
): (answers: Answers) => boolean {
  return (answers) => predicate(acceptedAnswers(questions, answers))
}

function acceptedAnswers<ReadableQuestions extends Questions<never>>(
  questions: ReadableQuestions,
  answers: Answers,
): AnswersOf<ReadableQuestions> {
  const accepted: Record<string, AnswerValue> = {}
  for (const [questionId, question] of Object.entries(questions)) {
    const value = answers[questionId]
    if (isAcceptedAnswer(question, value)) accepted[questionId] = value
  }
  if (!areAnswersOf(questions, accepted)) {
    throw new TechnicalError('While reading the answers of a standalone form, kept an answer its question refuses', {
      context: { questionIds: Object.keys(accepted) },
    })
  }
  return accepted
}

function areAnswersOf<ReadableQuestions extends Questions<never>>(
  questions: ReadableQuestions,
  answers: Answers,
): answers is Answers & AnswersOf<ReadableQuestions> {
  return Object.entries(answers).every(([questionId, value]) => {
    const question = questions[questionId]
    return question !== undefined && isAcceptedAnswer(question, value)
  })
}

function alwaysVisible(): boolean {
  return true
}

function checkScreens(
  formId: string,
  screens: readonly { readonly id: string; readonly questions: readonly string[] }[],
  questionIds: readonly string[],
) {
  const screenIds = screens.map((screen) => screen.id)
  if (new Set(screenIds).size !== screenIds.length) {
    throw new TechnicalError('While defining a standalone form, found two screens with the same id', {
      context: { formId, screenIds },
    })
  }

  const emptyScreenIds = screens.filter((screen) => screen.questions.length === 0).map((screen) => screen.id)
  if (emptyScreenIds.length > 0) {
    throw new TechnicalError('While defining a standalone form, found a screen without questions', {
      context: { formId, emptyScreenIds },
    })
  }

  const questionIdsOnScreens = screens.flatMap((screen) => screen.questions)
  for (const questionId of questionIds) {
    const screenCount = questionIdsOnScreens.filter((questionIdOnScreen) => questionIdOnScreen === questionId).length
    if (screenCount !== 1) {
      throw new TechnicalError(
        'While defining a standalone form, found a question on no screen or on several screens',
        { context: { formId, questionId, screenCount } },
      )
    }
  }

  if (questionIdsOnScreens.some((questionId, index) => questionId !== questionIds[index])) {
    throw new TechnicalError('While defining a standalone form, found screens that list the questions out of order', {
      context: { formId, questionIds, questionIdsOnScreens },
    })
  }
}

function checkOptions(questions: Questions<never>) {
  for (const [questionId, question] of Object.entries(questions)) {
    switch (question.kind) {
      case 'single-choice': {
        const optionIds = question.options.map((option) => option.id)
        if (new Set(optionIds).size !== optionIds.length) {
          throw new TechnicalError(
            'While defining a standalone form, found a single-choice question with duplicate option ids',
            { context: { questionId, optionIds } },
          )
        }
        break
      }
    }
  }
}
